import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { httpRequest } from 'src/services/httpRequest';

export type PetApplicationsDialogPet = {
  PetID: number;
  PetName?: string;
  PetType?: string;
};

export type PetApplicationHistoryRow = {
  ApplicationID: number;
  ApplicationNumber?: string;
  ApplicationStatus?: string;
  IssuedDate?: string;
  PermitExpirationDate?: string;
  LicenseNumber?: string;
  LicenseStatus?: string;
};

type PetApplicationsDialogProps = {
  open: boolean;
  pet: PetApplicationsDialogPet | null;
  onClose: () => void;
  onViewApplication?: (applicationId: number) => void;
};

function formatDate (value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return !Number.isNaN(d.getTime()) ? format(d, 'MM/dd/yyyy') : '—';
}

function licenseStatusColor (
  status?: string
): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Expiring Soon':
      return 'warning';
    case 'Expired':
      return 'error';
    default:
      return 'default';
  }
}

export default function PetApplicationsDialog ({
  open,
  pet,
  onClose,
  onViewApplication
}: PetApplicationsDialogProps) {
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState<PetApplicationHistoryRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !pet?.PetID) {
      setApplications([]);
      setError(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await httpRequest(
          'GET',
          `/pet/petDetails/${pet.PetID}/applications`
        );
        if (cancelled) return;
        if (res?.status === 'success') {
          const list = res?.data?.applications ?? [];
          setApplications(Array.isArray(list) ? list : []);
        } else {
          setApplications([]);
          setError(res?.message || 'Failed to load applications');
        }
      } catch {
        if (!cancelled) {
          setApplications([]);
          setError('Failed to load applications');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, pet?.PetID]);

  const title = pet?.PetName
    ? `License applications — ${pet.PetName}${pet.PetType ? ` (${pet.PetType})` : ''}`
    : 'License applications';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center" sx={{ py: 3 }}>
            {error}
          </Typography>
        ) : applications.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            No license applications found for this pet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Application</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>License status</TableCell>
                  <TableCell>Issued</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>License</TableCell>
                  {onViewApplication ? (
                    <TableCell align="center">View</TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {applications.map((app) => (
                  <TableRow key={app.ApplicationID} hover>
                    <TableCell>{app.ApplicationNumber || app.ApplicationID}</TableCell>
                    <TableCell>{app.ApplicationStatus || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={app.LicenseStatus || '—'}
                        color={licenseStatusColor(app.LicenseStatus)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatDate(app.IssuedDate)}</TableCell>
                    <TableCell>{formatDate(app.PermitExpirationDate)}</TableCell>
                    <TableCell>{app.LicenseNumber || '—'}</TableCell>
                    {onViewApplication ? (
                      <TableCell align="center">
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          sx={{ cursor: 'pointer', fontWeight: 600 }}
                          onClick={() => onViewApplication(app.ApplicationID)}
                        >
                          Open
                        </Link>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}
